import { create } from 'zustand';
import { createJSONStorage, persist } from 'zustand/middleware';

// Create a no-op storage for server-side rendering
const noopStorage = {
  getItem: () => null,
  setItem: () => {},
  removeItem: () => {},
};

// Use localStorage so per-course skip counts persist across sessions
const getLocalStorage = () => {
  if (typeof window !== 'undefined') {
    return localStorage;
  }
  return noopStorage;
};

const SKIP_THRESHOLD_FOR_REQUIRED = 3;

interface LoginPromptStore {
  // Per-course count of how many times user closed the skippable modal (keyed by courseId)
  courseSkipCounts: Record<string, number>;
  // Whether the prompt modal is currently showing
  isPromptOpen: boolean;
  // Type of prompt to show: 'skippable' or 'required' (required after 3 dismissals for that course)
  promptType: 'skippable' | 'required' | null;

  // Actions
  getSkipCount: (courseId: string) => number;
  incrementSkipCount: (courseId: string) => void;
  openPrompt: (type: 'skippable' | 'required') => void;
  closePrompt: () => void;
  resetAll: () => void;
  shouldShowRequired: (courseId: string) => boolean;
}

export const useLoginPromptStore = create<LoginPromptStore>()(
  persist(
    (set, get) => ({
      courseSkipCounts: {},
      isPromptOpen: false,
      promptType: null,

      getSkipCount: (courseId: string) => {
        return get().courseSkipCounts[courseId] ?? 0;
      },

      incrementSkipCount: (courseId: string) => {
        set((state) => ({
          courseSkipCounts: {
            ...state.courseSkipCounts,
            [courseId]: (state.courseSkipCounts[courseId] ?? 0) + 1,
          },
        }));
      },

      shouldShowRequired: (courseId: string) => {
        return (get().courseSkipCounts[courseId] ?? 0) >= SKIP_THRESHOLD_FOR_REQUIRED;
      },

      openPrompt: (type: 'skippable' | 'required') => {
        set({ isPromptOpen: true, promptType: type });
      },

      closePrompt: () => {
        set({ isPromptOpen: false, promptType: null });
      },

      resetAll: () => {
        set({
          courseSkipCounts: {},
          isPromptOpen: false,
          promptType: null,
        });
        if (typeof window !== 'undefined') {
          localStorage.removeItem('login-prompt-store');
        }
      },
    }),
    {
      name: 'login-prompt-store',
      storage: createJSONStorage(() => getLocalStorage()),
      partialize: (state) => ({ courseSkipCounts: state.courseSkipCounts }),
    }
  )
);

export { SKIP_THRESHOLD_FOR_REQUIRED };
